import { REGULARS } from '../../../ai';
import { LONG_NIGHT, currentTable, type NightRun, type NightTable } from '../../../campaign';
import { t, type MessageKey } from '../../../i18n';
import { itemName } from '../../../app/describe';
import type { ShowItem } from './Item';

/** Words and names the run's screens share. */

const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];
export const roman = (n: number): string => ROMAN[n - 1] ?? String(n);

/** "Night II", "Closing Time" or "After Hours, hour 2". */
export const nightLabel = (night: number): string =>
  night <= LONG_NIGHT.NIGHTS
    ? t('night.nightN', { n: roman(night) })
    : night === LONG_NIGHT.NIGHTS + 1
      ? t('night.closingTime')
      : t('night.afterHours', { n: night - LONG_NIGHT.NIGHTS - 1 });

const ORDINALS = ['first', 'second', 'third', 'fourth', 'fifth', 'sixth', 'seventh', 'eighth'];

/**
 * A Night's title, as it is set alone between Nights (docs/LONG_NIGHT_ART_DIRECTION.md §11.16):
 * "The Second Night", "Closing Time", "The First Hour".
 */
export function nightTitle(night: number): string {
  if (night <= LONG_NIGHT.NIGHTS)
    return t(`night.title.${ORDINALS[night - 1] ?? 'first'}` as MessageKey);
  if (night === LONG_NIGHT.NIGHTS + 1) return t('night.closingTime');
  const hour = night - LONG_NIGHT.NIGHTS - 1;
  return t(`night.hour.${ORDINALS[hour - 1] ?? 'first'}` as MessageKey);
}

export const tableKindName = (kind: NightTable['kind']): string => t(`night.table.${kind}`);

/**
 * Where a run in progress stands, for the menu's Resume: "Night II · Big Table", "Night I · at the
 * Counter", "Closing Time". Null when no run is under way.
 */
export function runWhere(run: NightRun | null | undefined): string | null {
  if (!run || run.phase === 'won' || run.phase === 'lost') return null;
  const night = nightLabel(run.night);
  if (run.phase === 'counter') return t('menu.nightAt.counter', { night });
  if (run.phase === 'spoils' || run.phase === 'detour')
    return t('menu.nightAt.afterHigh', { night });
  const kind = run.board[run.table] ? currentTable(run).kind : null;
  return !kind || kind === 'closing' ? night : `${night} · ${tableKindName(kind)}`;
}

export const opponentName = (table: NightTable): string => {
  if (table.kind === 'closing') return t('night.broker');
  if (table.regular !== undefined) return REGULARS[table.regular]?.name ?? t('night.broker');
  if (table.patron) return itemName(`patron.${table.patron}`);
  return t('night.brokerMan');
};

/** Who sits at a table, as a pixel portrait's emblem key (the Night board, the table's sheet). */
export const opponentPortrait = (table: NightTable): string => {
  if (table.kind === 'closing') return 'portrait.broker';
  if (table.regular !== undefined) return `portrait.regular.${table.regular}`;
  if (table.patron) return `portrait.${table.patron}`;
  return 'portrait.broker-man';
};

/** The one prop a table's opponent is known by (§12.11), if any. */
export const opponentProp = (table: NightTable): string | undefined =>
  table.kind === 'high' && table.regular !== undefined
    ? `regular.${table.regular}`
    : table.patron
      ? `patron.${table.patron}`
      : undefined;

export const today = (): string => new Date().toISOString().slice(0, 10);

/** A sheet that only explains something (a head start, a House Rule, a Favor…). */
export const info = (
  kindLabel: string,
  name: string,
  text: string,
  icon: ShowItem['icon'] = 'info',
  object?: ShowItem['object'],
): ShowItem => ({ kindLabel, name, text, icon, ...(object ? { object } : {}) });
